import React from 'react'
import NextImage from 'next/image'
import { Trans, useTranslation } from 'next-i18next'
import Box from '@mui/material/Box'
import { EmtTypography } from 'emt-components-v2'
import { useIsQueryOwner, useIsResponsive } from '@/hooks'
import { Routes, SpecificModules } from '@/assets/configs'
import VariantImages from '@/assets/VariantImages'
import LinkButton from '@/components/EMT/LinkButton'

// Requires the `emergency-pet-care-relay` namespace in the page's server side translations
const NS = 'emergency-pet-care-relay'

const partners = [
  { src: VariantImages.PurinaWhiteLogo, alt: 'Purina' },
  { src: VariantImages.Logo, alt: 'Emprunte mon toutou' },
  { src: VariantImages.LogoSitterWhite, alt: 'Super Sitters' },
]

/** Logos keep their ratio and shrink below `height` when the row is too narrow. */
const Partners: React.FC<{ height: number }> = ({ height }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
    {partners.map(({ src, alt }) => (
      <Box key={alt} sx={{ flex: '0 1 auto', minWidth: 0, display: 'flex' }}>
        <NextImage
          src={src}
          alt={alt}
          height={height}
          style={{ width: 'auto', height: 'auto', maxWidth: '100%', maxHeight: `${height}px` }}
        />
      </Box>
    ))}
  </Box>
)

type Props = {
  /** Use the mobile layout whatever the screen size (e.g. in a narrow sidebar) */
  forceMobile?: boolean
  sx?: object
}

/** Promotional banner linking to the Emergency Pet Care Relay page. */
export const EmergencyPetCareRelayBanner: React.FC<Props> = ({ forceMobile = false, sx }) => {
  const { t } = useTranslation(NS)
  const [isMobileScreen, isTablet] = useIsResponsive()
  const [, , appendQueryType] = useIsQueryOwner()
  const isMobile = forceMobile || isMobileScreen

  if (!SpecificModules.others.emergencyPetCareRelay) return null

  // Partners are moved under the button when there is not enough room on the right
  const isCompact = isMobile || isTablet

  return (
    <Box
      sx={{
        flexShrink: 0,
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '18px',
        backgroundColor: '#34303E',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'flex-start' : 'center',
        gap: isMobile ? '24px' : '40px',
        // On mobile, % padding follows the container width (35px from a 350px wide container, down to 20px)
        padding: isMobile ? 'clamp(20px, 10%, 35px)' : '50px',
        ...sx,
      }}
    >
      <NextImage
        src={isMobile ? VariantImages.EmergencyPetCareRelayBannerMobile : VariantImages.EmergencyPetCareRelayBanner}
        alt=""
        fill
        quality={100}
        sizes={isMobileScreen ? '100vw' : isMobile ? '600px' : '1200px'}
        style={{ objectFit: 'cover', objectPosition: isMobile ? 'center' : 'right center' }}
      />

      {/* Content above the background image */}
      <Box sx={{ position: 'relative', flexShrink: 0 }}>
        <NextImage
          src={VariantImages.EmergencyPetCareRelayLogo}
          alt={t('title')}
          style={{ width: isMobile ? '100px' : '160px', height: 'auto', display: 'block' }}
        />
      </Box>

      <Box
        sx={{
          position: 'relative',
          flex: 1,
          width: isMobile ? '100%' : 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          rowGap: isMobile ? '24px' : '16px',
        }}
      >
        <EmtTypography
          color="#fff"
          fontWeight={800}
          fontSize={isMobile ? '12px' : '18px'}
          sx={{ textTransform: 'uppercase', lineHeight: 1.6 }}
        >
          {/* `clone` repeats the red background + padding on each wrapped line */}
          <Box
            component="span"
            sx={{
              backgroundColor: 'primary.main',
              padding: '0px 6px',
              boxDecorationBreak: 'clone',
              WebkitBoxDecorationBreak: 'clone',
            }}
          >
            {t('intro.title')}
          </Box>
        </EmtTypography>

        <EmtTypography variant="body2" color="#fff" fontWeight={500}>
          <Trans t={t} i18nKey="intro.text" />
        </EmtTypography>

        <LinkButton
          variant="whiteOutlined"
          color="primary"
          fullWidth={isMobile}
          href={appendQueryType(Routes.EmergencyPetCareRelay)}
          sx={{ height: '48px', ...(isMobile ? {} : { alignSelf: 'flex-start', minWidth: '300px' }) }}
        >
          {t('banner.button')}
        </LinkButton>

        {isCompact && (
          <Box sx={{ marginTop: isMobile ? '8px' : 0, maxWidth: isMobile ? 'none' : '340px' }}>
            <Partners height={isMobile ? 32 : 28} />
          </Box>
        )}
      </Box>

      {!isCompact && (
        <Box sx={{ position: 'relative', flexShrink: 0, alignSelf: 'flex-end' }}>
          <Partners height={30} />
        </Box>
      )}
    </Box>
  )
}

export default EmergencyPetCareRelayBanner
